import {
    AuthContext,
    BaseButton,
    ClearinghouseVendorType,
    createRequestFilters,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    EraUploadDialog,
    getOptions,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderClearinghousesRequest,
    Popover,
    PopoverItem,
    ProviderClearinghouse,
    ProviderClearinghouseSortType,
} from '@enter/core';
import { mdiDotsVertical } from '@mdi/js';
import { Icon } from '@mdi/react';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseItemListingRequest, parseSortBy } from 'stores';
import { ClearinghouseStore } from 'stores/clearinghouse.store';
import * as S from './clearinghouses.styles';

interface IClearinghousesProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
    accountId?: string;
}

interface IClearinghousesState {
    loading: boolean;
}

const FETCH_GROUP = 'clearinghouses';

/**
 * Only the four fields `ProviderClearinghouse` actually carries. The support console shows key,
 * secret, external id, active, starred and tags as well — none of which the provider endpoint
 * returns, and none of which a provider has any business seeing.
 */
const columns: ITableColumn<ProviderClearinghouse>[] = [
    {
        id: 'id',
        Header: 'Id',
        accessor: 'id',
        Cell: DataTableCell('String'),
        disableSortBy: true,
        show: false,
    },
    {
        id: 'name',
        Header: 'Name',
        accessor: 'name',
        sortType: ProviderClearinghouseSortType.NAME,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'type',
        Header: 'Type',
        accessor: 'type',
        disableSortBy: true,
        Cell: ({ cell }) => <S.Cell>{cell.value}</S.Cell>,
    },
    {
        id: 'created',
        Header: 'Created',
        accessor: 'created',
        sortType: ProviderClearinghouseSortType.CREATED,
        Cell: DataTableCell('DateTime'),
    },
];

const filterSchemas: IFilterSchema<keyof ListProviderClearinghousesRequest>[] = [
    {
        label: 'Name',
        filterId: 'filterName',
        columnId: 'name',
        filterType: 'String',
    },
    {
        label: 'Type',
        filterId: 'filterType',
        columnId: 'type',
        filterType: 'Select',
        options: getOptions(ClearinghouseVendorType),
    },
];

class ClearinghousesPage extends React.Component<IClearinghousesProps, IClearinghousesState> {
    private presenter: DataTablePresenter<ProviderClearinghouse>;

    constructor(props: IClearinghousesProps) {
        super(props);
        this.state = { loading: false };
        this.presenter = new DataTablePresenter<ProviderClearinghouse>({
            name: FETCH_GROUP,
            data: [],
            columns: [...columns, this.actionsColumn],
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Clearinghouses';
    }

    public componentWillUnmount(): void {
        this.props.authContext.cancelGroup(FETCH_GROUP);
    }

    public render(): React.ReactElement {
        return (
            <PageLayout>
                <PageHeader
                    title="Clearinghouses"
                    subtitle={
                        this.isDemo
                            ? 'Clearinghouses are not part of demo data.'
                            : 'The clearinghouses this account submits through.'
                    }
                    hideDateRange={true}
                />
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={this.presenter}
                            isLoading={this.state.loading}
                            onFetch={this.onFetch}
                            getRowHref={this.getRowHref}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    /**
     * Drills into the account's ERAs. That list is not yet scoped to the clearinghouse clicked —
     * the destination says so itself; see ClearinghouseEras.
     */
    private getRowHref = (row: Row<ProviderClearinghouse>): string => {
        const { accountId } = this.props;
        const path = `clearinghouses/${row.original.id}`;
        return accountId ? `/${accountId}/${path}` : `/${path}`;
    };

    /**
     * The row's three-dot menu. The row is a link (getRowHref), so a click here must neither
     * bubble to the row nor let the anchor navigate — otherwise opening the menu also drills in.
     */
    private get actionsColumn(): ITableColumn<ProviderClearinghouse> {
        return {
            id: 'actions',
            Header: '',
            disableSortBy: true,
            width: 56,
            Cell: ({ row }) => (
                <S.ActionsCell onClick={this.stopRowNavigation}>
                    <Popover
                        align="right"
                        renderButton={(): React.ReactElement => (
                            <BaseButton
                                variant="unstyled"
                                aria-label={`Actions for ${row.original.name || 'clearinghouse'}`}>
                                <Icon path={mdiDotsVertical} size={0.8} />
                            </BaseButton>
                        )}>
                        <PopoverItem
                            disabled={!row.original.id}
                            onClick={(): void => this.openUploadDialog(row.original)}>
                            Upload ERA (835)
                        </PopoverItem>
                    </Popover>
                </S.ActionsCell>
            ),
        };
    }

    private stopRowNavigation = (e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
    };

    private openUploadDialog = (clearinghouse: ProviderClearinghouse): void => {
        // Unreachable in demo mode today (the table is empty there), and the fail-closed demo
        // middleware would refuse the POST anyway — but an upload is a write, so say it here too.
        const { id } = clearinghouse;
        if (this.isDemo || !id) {
            return;
        }
        const { dialogContext } = this.props;
        dialogContext.showDialog({
            props: {
                onClose: dialogContext.hideDialog,
                renderTitle: 'Upload ERA (835)',
                // A stray click outside must not discard a chosen file; Escape still closes.
                disableBackdropClick: true,
                disableEscapeKeyDown: false,
            },
            Component: (
                <EraUploadDialog
                    clearinghouseName={clearinghouse.name}
                    upload={(x12: string) => ClearinghouseStore.uploadEra(id, x12)}
                    onError={dialogContext.showError}
                    onClose={dialogContext.hideDialog}
                />
            ),
        });
    };

    /** Demo mode must never render real account data (NS-14), and there is no clearinghouse generator. */
    private get isDemo(): boolean {
        return !!this.props.rootStore?.isDemo();
    }

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        if (this.isDemo) {
            this.presenter.onDataUpdate([]);
            this.presenter.onDataTotalChange(0);
            return;
        }

        try {
            this.setState({ loading: true });
            this.props.authContext.cancelGroup(FETCH_GROUP);
            this.props.authContext.setGroup(FETCH_GROUP);

            const { pageIndex, pageSize, sortBy, validFilters } = fetchOptions;
            const { limit, offset, sort, order } = parseItemListingRequest({
                ...parseSortBy(sortBy, columns),
                page: pageIndex,
                pageSize,
            });

            const listing = await ClearinghouseStore.list({
                // omitActive: the shared filter builder adds `filterActive` by default, and
                // ListProviderClearinghousesRequest has no such parameter. This API ignores
                // unknown query params rather than rejecting them, so it would have travelled
                // silently and meant nothing.
                ...createRequestFilters(validFilters, true),
                limit,
                offset,
                sort: sort as ProviderClearinghouseSortType[] | undefined,
                order: order as ListProviderClearinghousesRequest['order'],
            });

            this.presenter.onDataUpdate(listing.items);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({ loading: false });
        } catch (err) {
            this.setState({ loading: false });
            this.props.dialogContext.showError(err);
        }
    };
}

export const Clearinghouses: React.FC<IObservedRootStore & { accountId?: string }> = inject('rootStore')(
    observer((props: IObservedRootStore & { accountId?: string }) => {
        const { rootStore, accountId } = props;
        if (!rootStore) {
            return <></>;
        }
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        return (
            <ClearinghousesPage
                accountId={accountId}
                authContext={authContext}
                dialogContext={dialogContext}
                rootStore={rootStore}
            />
        );
    }),
);
